import React from "react";
import {
  Badge,
  Code,
  CodeBlock,
  EnvTable,
  Note,
  Section,
  Table,
  Tbody,
  Thead,
  MONO,
  TD,
} from "./primitives";

const RESPONSE_SHAPE = `{
  "meta": {
    "total_rows": 42,
    "pagination": {
      "total": 5,
      "page": 1,
      "page_size": 10,
      "next": "https://.../resources/?page=2&page_size=10",
      "prev": null
    }
  },
  "request": {
    "method": "GET",
    "path": "https://.../resources/?page=1&page_size=10"
  },
  "response": {
    "code": 200,
    "data": [ { "resource_id": 391, "resource_name": "..." } ]
  },
  "error": null,
  "warnings": null
}`;

const FULL_EXAMPLE = `import requests

tenant_id = "09bb5b3b-dfce-473f-9615-47efe8193e80"

# --- Environment ---------------------------------------------------------
user_env = "stage"  # change to "prod" for production
if user_env == "stage":
    client_id = "your_stage_client_id"
    client_secret = "your_stage_client_secret"
    subscription_key = "your_stage_assetmetadata_subscription_key"
    scope = "api://stage-api-betm-com/.default"
    base_url = "https://api-stg.betm.com/"
else:
    client_id = "your_prod_client_id"
    client_secret = "your_prod_client_secret"
    subscription_key = "your_prod_assetmetadata_subscription_key"
    scope = "api://api-betm-com/.default"
    base_url = "https://api.betm.com/"

# --- Step 1: get a token -------------------------------------------------
token_url = f"https://login.microsoftonline.com/{tenant_id}/oauth2/v2.0/token"
token_response = requests.post(
    token_url,
    data={
        "grant_type": "client_credentials",
        "client_id": client_id,
        "client_secret": client_secret,
        "scope": scope,
    },
)
token_response.raise_for_status()
access_token = token_response.json()["access_token"]

# --- Step 2: call the service -------------------------------------------
headers = {
    "Authorization": f"Bearer {access_token}",
    "Content-Type": "application/json",
    "Product": "EXT",
    "Ocp-Apim-Subscription-Key": subscription_key,
}
response = requests.get(
    f"{base_url}metadata/resources/",
    headers=headers,
    params={"page": 1, "page_size": 10},
)
response.raise_for_status()

result = response.json()
print(f"Total rows: {result['meta']['total_rows']}")
print(result["response"]["data"])`;

const REF_DATA = [
  ["/ref-data/generation-subtype", "Generation subtypes"],
  ["/ref-data/communication-type", "Communication types"],
  ["/ref-data/control-type", "Control types"],
  ["/ref-data/scheduling-type", "Scheduling types"],
  ["/ref-data/timezones", "Timezones"],
  ["/ref-data/cities?page=&page_size=", "Cities, paginated"],
  ["/ref-data/states", "States"],
  ["/ref-data/dimensions", "Dimensions"],
  ["/ref-data/timeranges", "Time ranges"],
  ["/ref-data/frequencies", "Time intervals and frequencies"],
  ["/ref-data/betm-object-kv", "BETM object key/value pairs"],
  ["/ref-data/participant-status", "Participant status codes"],
  ["/ref-data/resource-type", "Resource types"],
  ["/ref-data/resource-status", "Resource status codes"],
  ["/ref-data/group-status", "Group status codes"],
  ["/ref-data/region", "Regions"],
  ["/ref-data/resources", "All assets with resources (deprecated)"],
];

const MetadataDoc: React.FC = () => (
  <div className="space-y-8">
    <p className="text-sm leading-6 text-gray-700">
      The master and reference data that defines AMP assets and related business entities: ISOs,
      reference data and resource metadata, plus the asset, company, participant and price-node
      surface behind the AMP screens. The service is published under the <Code>metadata</Code> path
      prefix.
    </p>

    <Section title="Environment">
      <EnvTable
        prodUrl="https://api.betm.com/metadata"
        stageUrl="https://api-stg.betm.com/metadata"
        product="AssetMetaData_Product"
      />
    </Section>

    <Section title="Entitlements">
      <p className="text-sm leading-6 text-gray-700">
        Beyond the gateway&apos;s token check, this service resolves the caller and checks
        asset-level entitlements. Requests for assets you are not entitled to return{" "}
        <Code>403 Forbidden</Code>. Two optional headers override the lookup defaults:{" "}
        <Code>X-Entitlement-Domain</Code> (defaults to <Code>amp</Code>) and{" "}
        <Code>X-Entitlement-Service</Code> (defaults to <Code>general_amp_service</Code>).
      </p>
      <Note tone="info">
        The service skips its entitlement middleware for health and discovery paths (
        <Code>/</Code>, <Code>/ping</Code>, <Code>/health</Code>, <Code>/docs</Code>,{" "}
        <Code>/redoc</Code>, <Code>/openapi.json</Code>), but the gateway still requires a
        subscription key for them. There is no anonymous health probe from outside the cluster.
      </Note>
    </Section>

    <Section title="ISO">
      <Table minWidth={780}>
        <Thead cols={["Method", "Path", "Description", "Availability"]} />
        <Tbody>
          <tr>
            <td className={TD}>GET</td>
            <td className={`${TD} font-mono`}>/iso/{'{iso_code}'}/assets</td>
            <td className={TD}>Assets for an ISO code.</td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>GET</td>
            <td className={`${TD} font-mono`}>/iso/{'{iso_code}'}/resources</td>
            <td className={TD}>Resources for an ISO code.</td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>GET</td>
            <td className={`${TD} font-mono`}>/iso/?fetch_mode=Default|All</td>
            <td className={TD}>
              List ISOs. <Code>All</Code> returns the unfiltered set, <Code>Default</Code> the
              filtered one.
            </td>
            <td className={TD}>
              <Badge tone="gap">Not published</Badge>
            </td>
          </tr>
        </Tbody>
      </Table>
    </Section>

    <Section title="Resources">
      <Table minWidth={780}>
        <Thead cols={["Method", "Path", "Description", "Availability"]} />
        <Tbody>
          <tr>
            <td className={TD}>GET</td>
            <td className={`${TD} font-mono`}>/resources/?page=&amp;page_size=</td>
            <td className={TD}>All resources, paginated.</td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>GET</td>
            <td className={`${TD} font-mono`}>/resources/{'{asset_id}'}?page=&amp;page_size=</td>
            <td className={TD}>Resources for one asset, paginated.</td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>GET</td>
            <td className={`${TD} font-mono`}>
              /resources/{'{asset_id}'}/{'{resource_id}'}
            </td>
            <td className={TD}>A single resource.</td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>POST</td>
            <td className={`${TD} font-mono`}>/resources/{'{asset_id}'}</td>
            <td className={TD}>
              Create a resource under an asset. Body: <Code>ResourceRequest</Code>.
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>PUT</td>
            <td className={`${TD} font-mono`}>
              /resources/{'{asset_id}'}/{'{resource_id}'}
            </td>
            <td className={TD}>
              Update a resource. Body: <Code>ResourceRequest</Code>.
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={TD}>DELETE</td>
            <td className={`${TD} font-mono`}>
              /resources/{'{asset_id}'}/{'{resource_id}'}
            </td>
            <td className={TD}>Delete a resource.</td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
        </Tbody>
      </Table>
    </Section>

    <Section title="Reference data">
      <Table minWidth={700}>
        <Thead cols={["Path (GET)", "Returns", "Availability"]} />
        <Tbody>
          {REF_DATA.map(([path, desc]) => (
            <tr key={path}>
              <td className={`${TD} ${MONO}`}>{path}</td>
              <td className={TD}>{desc}</td>
              <td className={TD}>
                <Badge tone="prod">Prod &amp; Stage</Badge>
              </td>
            </tr>
          ))}
          <tr>
            <td className={`${TD} ${MONO}`}>/ref-data/metric-config</td>
            <td className={TD}>Metric configuration reference data.</td>
            <td className={TD}>
              <Badge tone="gap">Not published</Badge>
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>/ref-data/vendor-sources/</td>
            <td className={TD}>Vendor sources.</td>
            <td className={TD}>
              <Badge tone="stage">Stage only</Badge>
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>
              /ref-data/vendor-sources/data-collection-frequencies
            </td>
            <td className={TD}>Data collection frequencies for vendor sources.</td>
            <td className={TD}>
              <Badge tone="stage">Stage only</Badge>
            </td>
          </tr>
          <tr>
            <td className={`${TD} ${MONO}`}>
              /ref-data/metric-config/{'{metric_id}'}/vendor-sources (GET, PUT)
            </td>
            <td className={TD}>Vendor sources mapped to one metric.</td>
            <td className={TD}>
              <Badge tone="stage">Stage only</Badge>
            </td>
          </tr>
        </Tbody>
      </Table>
    </Section>

    <Section title="Other endpoints on the gateway">
      <p className="text-sm leading-6 text-gray-700">
        Reachable through the gateway but not part of the integration contract; these back the AMP
        screens and their shapes may change without notice.
      </p>
      <Table minWidth={780}>
        <Thead cols={["Area", "Endpoints", "Availability"]} />
        <Tbody>
          <tr>
            <td className={`${TD} font-medium`}>Assets</td>
            <td className={`${TD} ${MONO}`}>
              GET/POST /assets/; GET/PUT/DELETE /assets/{'{asset_id}'}; GET /assets/all/resources
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={`${TD} font-medium`}>Companies</td>
            <td className={`${TD} ${MONO}`}>
              GET/POST/PUT /company/; DELETE /company/{'{company_id}'}; GET /company/
              {'{company_id}'}/assets; GET /company/{'{company_id}'}/domains; GET
              /company/all/setup; GET/POST /company/setup/{'{company_id}'}
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={`${TD} font-medium`}>Participants</td>
            <td className={`${TD} ${MONO}`}>
              GET/POST /participant/; GET/PUT/DELETE /participant/{'{participant_id}'}; GET
              /participant/{'{participant_id}'}/resources
            </td>
            <td className={TD}>
              <Badge tone="prod">Prod &amp; Stage</Badge>
            </td>
          </tr>
          <tr>
            <td className={`${TD} font-medium`}>Price nodes</td>
            <td className={`${TD} ${MONO}`}>GET/POST/PUT/DELETE /price-node</td>
            <td className={TD}>
              <Badge tone="stage">Stage only</Badge>
            </td>
          </tr>
        </Tbody>
      </Table>
    </Section>

    <Section title="Response">
      <p className="text-sm leading-6 text-gray-700">
        Every endpoint returns the shared <Code>GenericResponse</Code> envelope. On failure{" "}
        <Code>response</Code> is <Code>null</Code> and <Code>error</Code> carries the code and
        message. <Code>warnings</Code> is an optional list of non-fatal messages returned alongside a
        successful <Code>response</Code>, so treat it as present-but-usually-null rather than absent.
        Cities and Resources support <Code>page</Code> and <Code>page_size</Code>.
      </p>
      <CodeBlock code={RESPONSE_SHAPE} label="GenericResponse envelope" />
    </Section>

    <Section title="Worked example">
      <CodeBlock code={FULL_EXAMPLE} label="Python - authenticate and list resources" />
    </Section>
  </div>
);

export default MetadataDoc;
